import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";

export type NewsItem = {
  id: string;
  title: string;
  date: string;
  summary: string;
  category: string;
  body?: string;
  imageUrl?: string;
  linkUrl?: string;
  pinned?: boolean;
  urgent?: boolean;
  attachments?: Array<{
    id: string;
    name: string;
    downloadUrl?: string;
  }>;
};

const fallbackNews: NewsItem[] = [
  {
    id: "prekindergarten-open-house",
    title: "Pre-Kindergarten Open House",
    date: "2026-02-06",
    summary: "Prospective Pre-K families are invited to learn about ZCA's all-day, classically inspired preschool program.",
    category: "Admissions"
  },
  {
    id: "national-lutheran-schools-week",
    title: "National Lutheran Schools Week",
    date: "2026-01-26",
    summary: "Students celebrate Lutheran school life with special activities, school spirit, and Grandparents/VIP Day.",
    category: "Academics"
  },
  {
    id: "raisersupport",
    title: "RaiseRight and Rewards Programs Support ZCA",
    date: "2026-01-15",
    summary: "Families can support ZCA through RaiseRight gift cards, Box Tops, Casey's Rewards, and inkjet cartridge recycling.",
    category: "Support"
  }
];

function toIso(value: unknown) {
  try {
    const maybeTimestamp = value as { toDate?: () => Date; toMillis?: () => number } | null | undefined;
    if (!maybeTimestamp) return "";
    if (typeof maybeTimestamp.toDate === "function") return maybeTimestamp.toDate().toISOString();
    if (typeof maybeTimestamp.toMillis === "function") return new Date(maybeTimestamp.toMillis()).toISOString();
    const parsed = new Date(String(value));
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : "";
  } catch {
    return "";
  }
}

function toMillis(value: unknown) {
  try {
    const maybeTimestamp = value as { toDate?: () => Date; toMillis?: () => number } | null | undefined;
    if (!maybeTimestamp) return 0;
    if (typeof maybeTimestamp.toMillis === "function") return maybeTimestamp.toMillis();
    if (typeof maybeTimestamp.toDate === "function") return maybeTimestamp.toDate().getTime();
    const parsed = new Date(String(value));
    return Number.isFinite(parsed.getTime()) ? parsed.getTime() : 0;
  } catch {
    return 0;
  }
}

function isPubliclyActive(data: Record<string, unknown>) {
  const now = Date.now();
  const expiresAt = toMillis(data.expiresAt);
  const publishedAt = toMillis(data.publishedAt);
  const scheduledPublishAt = toMillis(data.scheduledPublishAt);

  if (expiresAt && expiresAt <= now) return false;
  if (publishedAt && publishedAt > now) return false;
  if (!publishedAt && scheduledPublishAt && scheduledPublishAt > now) return false;
  return true;
}

function mapNewsDoc(id: string, data: Record<string, unknown>): NewsItem {
  const publishDate =
    String(data.publishDateIso ?? "") ||
    toIso(data.publishedAt) ||
    toIso(data.scheduledPublishAt) ||
    toIso(data.createdAt);

  const tags = Array.isArray(data.tags) ? data.tags.map(String) : String(data.tags ?? "").split(",");
  const category = tags.map((tag) => tag.trim()).filter(Boolean)[0] || (data.urgent === true ? "Urgent" : "School News");
  const attachments = Array.isArray(data.attachments)
    ? data.attachments
        .map((item: any) => ({
          id: String(item?.id ?? item?.storagePath ?? item?.name ?? ""),
          name: String(item?.name ?? "Attachment"),
          downloadUrl: item?.downloadUrl ? String(item.downloadUrl) : undefined
        }))
        .filter((item) => item.id || item.downloadUrl)
    : undefined;

  return {
    id,
    title: String(data.title ?? "School News"),
    date: publishDate,
    summary: String(data.summary ?? data.excerpt ?? ""),
    body: String(data.body ?? ""),
    imageUrl: String(data.imageUrl ?? ""),
    linkUrl: String(data.linkUrl ?? ""),
    pinned: data.pinned === true,
    urgent: data.urgent === true,
    category,
    attachments
  };
}

async function getActiveSchoolYear() {
  if (!db) return null;
  try {
    const snap = await getDoc(doc(db, "settings", "schoolYear"));
    const activeYear = snap.exists() ? (snap.data() as any).activeYear : "";
    return typeof activeYear === "string" && activeYear.trim() ? activeYear.trim() : null;
  } catch {
    return null;
  }
}

export async function getNews(options: { useFallback?: boolean; limitCount?: number } = {}): Promise<NewsItem[]> {
  if (!db) return options.useFallback === false ? [] : fallbackNews;

  try {
    const activeYear = await getActiveSchoolYear();
    const snap = await getDocs(
      query(
        collection(db, "newsItems"),
        where("status", "==", "published"),
        orderBy("publishedAt", "desc"),
        limit(Math.min(Math.max(options.limitCount ?? 30, 1), 50))
      )
    );

    const activeDocs = snap.docs.filter((item) => isPubliclyActive(item.data()));
    let items = activeDocs.map((item) => mapNewsDoc(item.id, item.data()));
    if (activeYear) {
      items = items.filter((item) => {
        const source = activeDocs.find((docSnap) => docSnap.id === item.id)?.data() as any;
        return !source?.schoolYear || source.schoolYear === activeYear;
      });
    }

    items.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (b.date || "").localeCompare(a.date || "");
    });

    return items.length ? items : options.useFallback === false ? [] : fallbackNews;
  } catch (error) {
    console.warn("[public-web] Failed to load Firestore newsItems", error);
    return options.useFallback === false ? [] : fallbackNews;
  }
}

export async function getNewsItem(id: string): Promise<NewsItem | null> {
  if (!db || !id.trim()) return null;

  try {
    const activeYear = await getActiveSchoolYear();
    const snap = await getDoc(doc(db, "newsItems", id.trim()));
    if (!snap.exists()) return null;

    const data = snap.data() as Record<string, unknown>;
    if (data.status !== "published") return null;
    if (!isPubliclyActive(data)) return null;
    if (activeYear && typeof data.schoolYear === "string" && data.schoolYear && data.schoolYear !== activeYear) return null;

    return mapNewsDoc(snap.id, data);
  } catch (error) {
    console.warn("[public-web] Failed to load Firestore newsItem", error);
    return null;
  }
}

export function getFallbackNews() {
  return [
    ...fallbackNews
  ];
}
